import { useCallback, useEffect, useState } from 'react';

import {
  Connection,
  Edge,
  Node,
  NodeChange,
  NodeSelectionChange,
  OnSelectionChangeParams,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStoreApi,
} from 'reactflow';
import toast from 'react-hot-toast';
import 'reactflow/dist/style.css';
import { useSetRecoilState } from 'recoil';

import { FLOW_TYPE } from 'constants/flow';
import { selectedNodeState } from 'state/atoms';
import { FlowContextMenu, Model, ModelDirection } from 'types';
import useCopyPaste from './useCopyPaste';
import useAutoLayout from './useAutoLayout';
import useGroup from './useGroup';
import { checkDataNodeExist, getNodeId } from '../utils/nodeUtils';
import useTakeSnapshot from './useTakeSnapshot';

interface UseFlowProps {
  model: Model;
  modelDirection: ModelDirection;
}

const useFlow = ({ model, modelDirection }: UseFlowProps) => {
  const store = useStoreApi();

  const [, setConnectingHandle] = useState<EventTarget & Element>();
  const [contextMenu, setContextMenu] = useState<FlowContextMenu | undefined>(undefined);
  const [prevModelDirection, setPrevModelDirection] = useState(modelDirection);
  const setSelectedNode = useSetRecoilState(selectedNodeState);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node[]>(model.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge[]>(model.edges);

  const { getNode, getNodes, setViewport, screenToFlowPosition, deleteElements } = useReactFlow();

  const { cut, copy, paste } = useCopyPaste();
  const { group, unGroup, checkSourceTargetNode, toggleSourceTargetNode } = useGroup();
  const { autoLayout } = useAutoLayout({ direction: modelDirection === 'horizontal' ? 'LR' : 'TB' });

  const { takeSnapshot } = useTakeSnapshot();

  const [isNodesEdgesSelected, setIsNodesEdgesSelected] = useState(false);

  const resetSelectedElements = useCallback(
    (except?: { nodeId?: string; edgeId?: string }) => {
      store.getState().resetSelectedElements();

      if (except?.nodeId) {
        setNodes((prevNodes) => prevNodes.map((node) => ({ ...node, selected: node.id === except.nodeId })));
      }
      if (except?.edgeId) {
        setEdges((prevEdges) => prevEdges.map((edge) => ({ ...edge, selected: edge.id === except.edgeId })));
      }
    },
    [store, setNodes, setEdges]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenu(undefined);
  }, [setContextMenu]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const nodes = getNodes();

      const newNodeData = JSON.parse(event.dataTransfer.getData(FLOW_TYPE.NODE.NEW_NODE));
      if (newNodeData.type === FLOW_TYPE.NODE.DATA_NODE && checkDataNodeExist(nodes)) {
        return toast.error('현재 데이터 블록은 한 개만 사용할 수 있어요');
      }

      const newNode = {
        id: getNodeId(newNodeData.data.name),
        position: screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        }),
        ...newNodeData,
      } as Node;

      takeSnapshot();
      setNodes(nodes.concat(newNode));
    },
    [getNodes, setNodes, screenToFlowPosition, takeSnapshot]
  );

  const handleCustomNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const dataNodeChanges = changes.filter(
        (change) =>
          (change.type === 'select' || change.type === 'remove') &&
          getNode(change.id)?.type === FLOW_TYPE.NODE.DATA_NODE
      );

      if (dataNodeChanges.length > 0) {
        const change = dataNodeChanges[0];

        if ((change.type === 'select' && !change.selected) || change.type === 'remove') {
          setSelectedNode(undefined);
        } else {
          setSelectedNode(getNode((change as NodeSelectionChange).id));
        }
      }
      onNodesChange(changes);
    },
    [getNode, onNodesChange, setSelectedNode]
  );

  const handleConnectStart = useCallback(
    (event: React.MouseEvent<Element, MouseEvent> | React.TouchEvent<Element>) => {
      const handle = event.currentTarget;

      event.currentTarget.classList.add('connecting');
      setConnectingHandle(handle);
      resetSelectedElements();
      closeContextMenu();
    },
    [resetSelectedElements, closeContextMenu, setConnectingHandle]
  );

  const handleConnectEnd = useCallback(() => {
    setConnectingHandle((prev) => {
      if (prev) {
        prev.classList.remove('connecting');
      }
      return undefined;
    });
  }, [setConnectingHandle]);

  const handleConnect = useCallback(
    (connection: Connection) => {
      const sourceNode = getNode(connection.source as string);
      const targetNode = getNode(connection.target as string);

      if (sourceNode?.parentNode) {
        if (sourceNode.parentNode !== targetNode?.parentNode) {
          const parentNode = getNode(sourceNode.parentNode);

          if (!parentNode?.data.sourceNodeIds.includes(sourceNode.id)) {
            toast.error('OUT 블록만 그룹 밖의 블록과 연결 가능해요\n블록 메뉴에서 OUT 블록으로 설정해주세요');
            return false;
          }
        }
      }
      if (targetNode?.parentNode) {
        if (sourceNode?.parentNode !== targetNode?.parentNode) {
          const parentNode = getNode(targetNode.parentNode);

          if (!parentNode?.data.targetNodeIds.includes(targetNode.id)) {
            toast.error('IN 블록만 그룹 밖의 블록과 연결 가능해요\n블록 메뉴에서 IN 블록으로 설정해주세요');
            return false;
          }
        }
      }

      takeSnapshot();
      setEdges((prev) => addEdge({ ...connection, type: FLOW_TYPE.EDGE.ARROW_EDGE }, prev));
    },
    [getNode, setEdges, takeSnapshot]
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      resetSelectedElements();
      setContextMenu({
        pos: { x: event.clientX, y: event.clientY },
        onPaste: () => {
          paste();
          takeSnapshot();
        },
      });
    },
    [resetSelectedElements, paste, setContextMenu, takeSnapshot]
  );

  const handleNodeContextMenu = useCallback(
    (event: React.MouseEvent, targetNode: Node) => {
      event.preventDefault();
      event.stopPropagation();

      if (!targetNode.selected) {
        resetSelectedElements({ nodeId: targetNode.id });
      }

      if (targetNode.type === FLOW_TYPE.NODE.DATA_NODE) {
        setSelectedNode(targetNode);
      }

      const checkSourceTarget = checkSourceTargetNode(targetNode.id);

      setContextMenu({
        pos: { x: event.clientX, y: event.clientY },
        onDelete: () => {
          deleteElements({ nodes: [targetNode] });
        },
        onCut: () => {
          cut();
        },
        ...(targetNode.type !== FLOW_TYPE.NODE.DATA_NODE && {
          onCopy: () => {
            copy();
          },
        }),
        ...(targetNode.type === FLOW_TYPE.NODE.GROUP_NODE && {
          onUnGroup: () => {
            takeSnapshot();
            unGroup(targetNode.id);
          },
        }),
        ...(checkSourceTarget && {
          [checkSourceTarget.isSource ? 'onUnSourceNode' : 'onSourceNode']: () => {
            takeSnapshot({ updateCode: false });
            toggleSourceTargetNode(targetNode.id, 'source');
          },
          [checkSourceTarget.isTarget ? 'onUnTargetNode' : 'onTargetNode']: () => {
            takeSnapshot({ updateCode: false });
            toggleSourceTargetNode(targetNode.id, 'target');
          },
        }),
      });
    },
    [
      copy,
      cut,
      resetSelectedElements,
      checkSourceTargetNode,
      toggleSourceTargetNode,
      setSelectedNode,
      setContextMenu,
      deleteElements,
      takeSnapshot,
      unGroup,
    ]
  );

  const handleEdgeContextMenu = useCallback(
    (event: React.MouseEvent, targetEdge: Edge) => {
      event.preventDefault();
      event.stopPropagation();

      if (!targetEdge.selected) {
        resetSelectedElements({ edgeId: targetEdge.id });
      }

      setContextMenu({
        pos: { x: event.clientX, y: event.clientY },
        onDelete: () => {
          takeSnapshot();
          deleteElements({ edges: [targetEdge] });
        },
      });
      setSelectedNode(undefined);
    },
    [resetSelectedElements, setContextMenu, setSelectedNode, takeSnapshot, deleteElements]
  );

  const handleSelectionContextMenu = useCallback(
    (event: React.MouseEvent, selectedNodes: Node[]) => {
      event.preventDefault();
      event.stopPropagation();

      setContextMenu({
        pos: { x: event.clientX, y: event.clientY },
        onDelete: () => {
          deleteElements({ nodes: selectedNodes });
        },
        onCopy: () => {
          copy();
        },
        onCut: () => {
          cut();
        },
        onGrouping: () => {
          group(
            selectedNodes.filter((node) => !node.parentNode),
            takeSnapshot
          );
        },
      });
    },
    [copy, cut, setContextMenu, deleteElements, group, takeSnapshot]
  );

  // const handleNodeDragStart = useCallback(() => {
  //   setWaitElements({
  //     nodes: getNodes(),
  //     edges: getEdges(),
  //   });
  //   closeContextMenu();
  // }, [getNodes, getEdges, closeContextMenu]);

  // const handleNodeDrag = useCallback(() => {
  //   setIsDragging(true);
  // }, [setIsDragging]);

  // const handleNodeDragStop = useCallback(() => {
  //   if (isDragging && waitElements) {
  //     takeSnapshot({ save: false, nodes: waitElements.nodes, edges: waitElements.edges });
  //   }
  //   setIsDragging(false);
  //   setWaitElements(undefined);
  // }, [isDragging, waitElements, takeSnapshot, setIsDragging, setWaitElements]);

  const handleSelectionChange = useCallback((params: OnSelectionChangeParams) => {
    setIsNodesEdgesSelected(params.nodes.length > 0 && params.edges.length > 0);
  }, []);

  const handleNodeDragStart = useCallback(() => {
    takeSnapshot({ updateCode: false });
  }, [takeSnapshot]);

  const handleNodesDelete = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  const handleEdgesDelete = useCallback(() => {
    if (!isNodesEdgesSelected) {
      takeSnapshot();
    }
  }, [isNodesEdgesSelected, takeSnapshot]);

  useEffect(() => {
    if (modelDirection !== prevModelDirection) {
      autoLayout();
      setPrevModelDirection(modelDirection);
    }
  }, [modelDirection, prevModelDirection, autoLayout, setPrevModelDirection]);

  useEffect(() => {
    setNodes(model.nodes);
    setEdges(model.edges);
    setViewport(model.viewport);
  }, [model, setNodes, setEdges, setViewport]);

  return {
    contextMenu,
    nodes,
    edges,
    closeContextMenu,
    onConnect: handleConnect,
    onConnectStart: handleConnectStart,
    onConnectEnd: handleConnectEnd,
    onContextMenu: handleContextMenu,
    onNodesChange: handleCustomNodesChange,
    onNodeDragStart: handleNodeDragStart,
    // onNodeDragStop: handleNodeDragStop,
    // onNodeDrag: handleNodeDrag,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
    onEdgesChange,
    onSelectionStart: closeContextMenu,
    onPaneClick: closeContextMenu,
    onSelectionContextMenu: handleSelectionContextMenu,
    onSelectionDragStart: handleNodeDragStart,
    onNodeContextMenu: handleNodeContextMenu,
    onEdgeContextMenu: handleEdgeContextMenu,
    onNodesDelete: handleNodesDelete,
    onEdgesDelete: handleEdgesDelete,
    onSelectionChange: handleSelectionChange,
  };
};

export default useFlow;
